import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Grid,
} from '@mui/material';

/**
 * Enterprise-grade loading skeleton components
 * Provides smooth loading experiences for different content types
 */

// Table skeleton
export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <Box sx={{ width: '100%' }}>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box
        key={rowIndex}
        sx={{
          display: 'flex',
          gap: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            variant="text"
            width={`${100 / columns}%`}
            height={24}
          />
        ))}
      </Box>
    ))}
  </Box>
);

// Card skeleton
export const CardSkeleton = ({ count = 3 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Chart skeleton
export const ChartSkeleton = ({ height = 300 }) => (
  <Box sx={{ width: '100%', height }}>
    <Stack spacing={1} sx={{ height: '100%', justifyContent: 'flex-end' }}>
      <Skeleton variant="text" width="30%" height={24} />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flex: 1 }}>
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            sx={{
              flex: 1,
              height: `${Math.random() * 60 + 40}%`,
              borderRadius: 1,
            }}
          />
        ))}
      </Box>
      <Skeleton variant="text" width="100%" height={20} />
    </Stack>
  </Box>
);

// List skeleton
export const ListSkeleton = ({ items = 5 }) => (
  <Stack spacing={2}>
    {Array.from({ length: items }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Stack>
);

// Stats skeleton
export const StatsSkeleton = ({ count = 4 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} sm={6} md={3} key={index}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={36} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="40%" height={16} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Detail view skeleton
export const DetailSkeleton = () => (
  <Stack spacing={3}>
    <Box>
      <Skeleton variant="text" width="40%" height={36} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="60%" height={20} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
    </Box>
    <Card>
      <CardContent>
        <Stack spacing={2}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="text" width="30%" height={20} />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
    <Card>
      <CardContent>
        <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
        <ChartSkeleton height={200} />
      </CardContent>
    </Card>
  </Stack>
);

// Generic page skeleton
export const PageSkeleton = () => (
  <Box>
    {/* Header skeleton */}
    <Box sx={{ mb: 3 }}>
      <Skeleton variant="text" width="30%" height={40} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="50%" height={20} />
    </Box>

    {/* Filters skeleton */}
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
    </Box>

    {/* Content skeleton */}
    <Card>
      <CardContent>
        <TableSkeleton rows={10} columns={5} />
      </CardContent>
    </Card>
  </Box>
);

// Default export with all skeleton types
const LoadingSkeleton = {
  Table: TableSkeleton,
  Card: CardSkeleton,
  Chart: ChartSkeleton,
  List: ListSkeleton,
  Stats: StatsSkeleton,
  Detail: DetailSkeleton,
  Page: PageSkeleton,
};

export default LoadingSkeleton;
